import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { getNestedValue, interpolate, translations } from './translations';

const defaultLocale = 'en';

const defaultLanguageContext = {
  locale: defaultLocale,
  setLocale: () => {},
  t: (key, params = {}) => {
    const fallbackText = getNestedValue(translations[defaultLocale], key) || key;
    return interpolate(fallbackText, params);
  },
};

const LanguageContext = createContext(defaultLanguageContext);

export const LanguageProvider = ({ children }) => {
  const [locale, setLocale] = useState(() => {
    try {
      return localStorage.getItem('preferredLocale') || defaultLocale;
    } catch (error) {
      return defaultLocale;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('preferredLocale', locale);
    } catch (error) {
      // Ignore storage errors in restricted environments.
    }
  }, [locale]);

  const t = (key, params = {}) => {
    const possibleLocales = [locale, defaultLocale];
    const text = possibleLocales.reduce((result, currentLocale) => {
      if (result !== undefined) {
        return result;
      }

      const translationSet = translations[currentLocale];
      return getNestedValue(translationSet, key);
    }, undefined);

    const fallbackText = getNestedValue(translations[defaultLocale], key) || key;
    const resolvedText = text || fallbackText;

    return interpolate(resolvedText, params);
  };

  const value = useMemo(() => ({
    locale,
    setLocale,
    t,
  }), [locale]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLocale = () => useContext(LanguageContext);

export const getAvailableLocales = () => [{ value: 'en', label: 'English' }, { value: 'es', label: 'Español' }];
